import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatSort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { selectUnit } from '@dashboards/selectors/dashboards.selectors';
import { InspectionsBaseComponent } from '@main-application/inspections/components/inspections-base.component';
import { InspectionModel } from '@main-application/inspections/models/inspection.model';
import { InspectionService } from '@main-application/inspections/services/inspection.service';
import { RestUnitModel } from '@shared/interfaces/unit.interface';
import { RestTemplateModel } from '@template/models/rest-template-model.interface';
import { loadAllUserTemplates } from '@template/store/template.actions';
import { selectTemplates } from '@template/store/template.selectors';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { ModalsService } from '@ui-components/modals/modals.service';

@UntilDestroy()
@Component({
  selector: 'app-unit-details-inspections',
  templateUrl: './unit-details-inspections.component.html',
  styleUrls: [
    '../../../../../inspections/components/inspections-base.component.scss',
    './unit-details-inspections.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitDetailsInspectionsComponent extends InspectionsBaseComponent implements OnInit {
  sortDirection: SortDirection = 'desc';
  sortActive = 'completedAt';
  inspectionsSource: MatTableDataSource<InspectionModel>;
  unit: RestUnitModel;
  templateList: RestTemplateModel[];

  @Input()
  readonly displayedColumns = [
    'inspectionType',
    'turboMode',
    'status',
    'rooms',
    'dueDate',
    'completedAt',
    'assignee',
    'actions',
  ];

  @ViewChild(MatSort, { static: false }) set content(sort: MatSort) {
    if (sort) {
      if (this.inspectionsSource) {
        this.inspectionsSource.sort = sort;
        this.cdr.detectChanges();
      }
    }
  }

  constructor(
    cdr: ChangeDetectorRef,
    router: Router,
    snackbarService: SnackbarService,
    inspectionService: InspectionService,
    modalService: ModalsService,
    private store: Store<{}>
  ) {
    super(cdr, inspectionService, snackbarService, modalService, router);
  }

  ngOnInit(): void {
    this.store.dispatch(loadAllUserTemplates());

    combineLatest([this.store.select(selectUnit), this.store.select(selectTemplates)])
      .pipe(
        untilDestroyed(this),
        filter(([unit, templates]) => !!unit && !!templates),
        tap(([unit, templateList]) => {
          this.unit = unit;
          this.templateList = templateList;
          this.initDataSource();
        })
      )
      .subscribe();
  }

  private initDataSource() {
    this.inspectionService.getBeUnitId(this.unit.id).subscribe(inspections => {
      this.inspectionsSource = new MatTableDataSource(
        inspections.map(
          e =>
            new InspectionModel(
              e,
              this.templateList.find(t => t.id == e.inspectionTemplateId)
            )
        )
      );
      this.inspectionsSource.sortingDataAccessor = this.tableSortPredicate.bind(this);
      this.cdr.detectChanges();
    });
  }

  deleteInspection(inspectionModel: InspectionModel) {
    super.delete(inspectionModel).subscribe(() => {
      this.snackbarService.success('Inspection deleted');
      this.initDataSource();
    });
  }
}
